/**
 * `v-infinite-scroll="loadMore"` - call the handler as the element nears the bottom of
 * whatever scrolls it.
 *
 * A port of vue-infinite-scroll 2, which this replaces. The library bound its scroll
 * listener from `vm.$on('hook:mounted')` and released it from `vm.$on('hook:beforeDestroy')`,
 * and vue 3 has neither - `hook:` events throw once INSTANCE_EVENT_HOOKS is retired, which
 * is what it was holding hostage. A directive's own `mounted` / `unmounted` do the same job
 * with nothing to opt out of.
 *
 * The measuring is the library's, unchanged, including the two cases it distinguishes: the
 * element may *be* the scrolling box (a table with `overflow-y: auto`), or it may be a
 * marker sitting at the bottom of one. Only `infinite-scroll-distance` is carried over -
 * `-disabled`, `-immediate-check`, `-throttle-delay` and `-listen-for-event` were supported
 * by the library and used by nothing here.
 *
 * The handler is expected to be safe to call repeatedly: it fires on every throttled scroll
 * event while the element is in range, exactly as the library did, and InfiniteScrollMixin's
 * `loadMore` returns early unless there is a page to fetch.
 *
 * This also replaced vue-infinite-loading, a second library doing the same job as a
 * component - the four log popups that used `<infinite-loading @infinite="...">` mixed in
 * InfiniteScrollMixin anyway, so all the component contributed over `loadMore` was its own
 * spinner, now an `fa-spinner` on `loading && initialized`. Note where the directive goes
 * in a popup: on the list container, which carries `max-height` + `overflow-y: auto` and is
 * therefore its own scroll box. It cannot go on a bare sentinel div and be left to find the
 * `.modal`, because simplePopupFactory mounts the popup into the body *before* it is opened
 * - at `mounted` the modal is still `display: none` with no `.modal-open` on the body, so
 * bootstrap's `.modal-open .modal { overflow-y: auto }` has not applied yet and the walk up
 * would settle on `window`.
 */
const CTX = Symbol('infiniteScroll');
const THROTTLE_DELAY = 200;
const BIND_RETRIES = 10;

/** Leading edge, then trailing - the library's own shape, so the feel does not change. */
function throttle (fn, delay) {
    let lastRun = 0;
    let timer = null;
    return function throttled () {
        const now = Date.now();
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        const wait = delay - (now - lastRun);
        if (!lastRun || wait < 0) {
            lastRun = now;
            fn();
        } else {
            timer = setTimeout(() => {
                lastRun = Date.now();
                fn();
            }, wait);
        }
    };
}

/** The nearest scrolling ancestor, the element itself included, or `window`. */
function scrollTargetOf (element) {
    let node = element;
    while (node && node.nodeType === 1 && node.tagName !== 'HTML' && node.tagName !== 'BODY') {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'scroll' || overflowY === 'auto') {
            return node;
        }
        node = node.parentNode;
    }
    return window;
}

const scrollTopOf = target => (target === window
    ? Math.max(window.pageYOffset || 0, document.documentElement.scrollTop)
    : target.scrollTop);

const visibleHeightOf = target => (target === window
    ? document.documentElement.clientHeight
    : target.clientHeight);

const topOf = element => (element === window
    ? scrollTopOf(window)
    : element.getBoundingClientRect().top + scrollTopOf(window));

function distanceOf (element) {
    const raw = element.getAttribute('infinite-scroll-distance');
    const distance = Number(raw);
    return raw && !isNaN(distance) ? distance : 0;
}

function check (context) {
    const { el, target, distance, handler } = context;
    const scrollTop = scrollTopOf(target);
    const viewportBottom = scrollTop + visibleHeightOf(target);

    const reached = target === el
        ? el.scrollHeight - viewportBottom <= distance
        : viewportBottom + distance >= topOf(el) - topOf(target) + el.offsetHeight + scrollTop;

    if (reached && handler) {
        handler();
    }
}

function bind (context, attempt = 0) {
    // `getComputedStyle` reads nothing useful off an element that is not in the document
    // yet, and picking the scroll target wrong is silent - the handler simply never fires
    if (!context.el.isConnected) {
        if (attempt < BIND_RETRIES) {
            setTimeout(() => bind(context, attempt + 1), 50);
        }
        return;
    }
    context.target = scrollTargetOf(context.el);
    context.distance = distanceOf(context.el);
    context.listener = throttle(() => check(context), THROTTLE_DELAY);
    context.target.addEventListener('scroll', context.listener, { passive: true });
    // the list may already be shorter than the box it sits in
    check(context);
}

export default {
    mounted (el, binding) {
        const context = { el, handler: binding.value, target: null, listener: null };
        el[CTX] = context;
        bind(context);
    },

    updated (el, binding) {
        if (el[CTX]) {
            el[CTX].handler = binding.value;
        }
    },

    unmounted (el) {
        const context = el[CTX];
        if (context && context.listener) {
            context.target.removeEventListener('scroll', context.listener);
        }
        // whatever happens, stop calling into a component that is going away
        if (context) {
            context.handler = null;
        }
        delete el[CTX];
    },
};
